import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Stack, Typography } from "@mui/joy";
import { useNavigate } from "@tanstack/react-router";
import { ApiLeague, ApiLeagueSummary } from "griptonite-api-client";
import { useEffect, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";

import {
  useCreateLeagueMutation,
  useLeagueQuery,
  useUpdateLeagueMutation,
} from "@/hooks/competitions";
import { useCurrentVenueQuery } from "@/hooks/venues";
import { handleFormError } from "@/utils/handleFormError";

import FormErrorAlert from "../FormErrorAlert";
import { MediaSelectInput } from "../forms/MediaInput";
import TextInput, { TextareaInput } from "../forms/TextInput";
import BottomButtons from "./BottomButtons";
import {
  DetailsFormSchema,
  DetailsFormValues,
  detailsFormValuesToPayload,
  leagueToDetailsFormValues,
} from "./forms";

const LeagueDetailsForm = ({ leagueUuid }: { leagueUuid?: string; startDate?: string }) => {
  const isCreating = !leagueUuid;

  const { data: venue } = useCurrentVenueQuery();

  const { data: leagueSummaryOrDetail, isFetching, isPending } = useLeagueQuery(leagueUuid);

  const detailPending = (!isCreating && isPending) || (!leagueSummaryOrDetail?.urls && isFetching);

  const league = leagueSummaryOrDetail as ApiLeague | ApiLeagueSummary;

  const defaultValues = useMemo(() => leagueToDetailsFormValues(league), [league]);

  const methods = useForm({
    mode: "onBlur",
    resolver: zodResolver(DetailsFormSchema),
    defaultValues,
  });

  useEffect(() => {
    methods.reset(defaultValues);
  }, [defaultValues, methods]);

  const createMutation = useCreateLeagueMutation();
  const updateMutation = useUpdateLeagueMutation(league?.uuid, venue?.uuid);
  const navigate = useNavigate();

  if (detailPending) {
    return null;
  }

  const onSubmit = async (values: DetailsFormValues) => {
    if (!venue) {
      return;
    }
    const payload = detailsFormValuesToPayload(values, venue.uuid);
    if (isCreating) {
      const league = await createMutation.mutateAsync(
        { ...payload },
        {
          onError: async (error) => {
            handleFormError(error, methods);
          },
        },
      );
      navigate({
        to: "/gym/$venueUuid/events/series/$leagueUuid/profile/categories",
        params: {
          venueUuid: venue.uuid,
          leagueUuid: league.uuid,
        },
        replace: true,
      });
    } else {
      const league = await updateMutation.mutateAsync(payload, {
        onError: async (error) => {
          handleFormError(error, methods);
        },
      });
      if (league.isHidden) {
        navigate({
          to: "/gym/$venueUuid/events/series/$leagueUuid/profile/categories",
          params: {
            venueUuid: venue.uuid,
            leagueUuid: league.uuid,
          },
          replace: true,
        });
      }
    }
  };

  return (
    <Stack
      sx={{ padding: 1.5, gap: 1, flex: 1, overflowY: "auto" }}
      component="form"
      autoComplete="off"
      onSubmit={methods.handleSubmit(onSubmit as (vals: typeof defaultValues) => Promise<void>)}
    >
      <FormProvider {...methods}>
        <FormErrorAlert />
        <Stack sx={{ flexGrow: 1 }} spacing={1}>
          <FormFields />
        </Stack>
        <BottomButtons
          formState={methods.formState}
          nextLink={
            league
              ? {
                  to: "/gym/$venueUuid/events/series/$leagueUuid/profile/categories",
                  from: "/gym/$venueUuid/events/series/$leagueUuid/profile/details",
                  replace: true,
                }
              : undefined
          }
          league={league}
        />
      </FormProvider>
    </Stack>
  );
};

const FormFields = () => {
  return (
    <>
      <TextInput name="name" label="Name" />
      <TextareaInput name="description" label="Description" />
      <MediaSelectInput name="media" label="Media" multiple accept="image/*,video/*" />
      <Box>
        <Typography fontSize={15} marginBottom={1} fontWeight="bold">
          Links
        </Typography>
        <Stack gap={1} sx={{ label: { fontSize: 13 } }}>
          <TextInput name="websiteUrl" label="Website" />
          <TextInput name="facebookUrl" label="Facebook" />
          <TextInput name="instagramUrl" label="Instagram" />
        </Stack>
      </Box>
    </>
  );
};

export default LeagueDetailsForm;
